import { Quaternion, Vector3 } from 'three'
import type { ReactNode } from 'react'
import { Peca } from '../componentes/Peca'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'

function ConjuntoSuspensao({ anatomia, id, x, children }: { anatomia: boolean; id: string; x: number; children: ReactNode }) {
  return anatomia ? <Peca id={id} deslocamentoAfastado={[Math.sign(x) * 0.25, 0.08, 0]}>{children}</Peca> : children
}

function LigacaoSuspensao({ de, para }: { de: [number, number, number]; para: [number, number, number] }) {
  const a = new Vector3(...de), b = new Vector3(...para)
  return <mesh name="braco-suspensao" position={a.clone().add(b).multiplyScalar(0.5)}
    quaternion={new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), b.clone().sub(a).normalize())}>
    <cylinderGeometry args={[0.021, 0.021, a.distanceTo(b), 8]} />
    <meshStandardMaterial color="#7d8c9b" metalness={0.65} roughness={0.4} />
  </mesh>
}

interface Props {
  perfil: PerfilVeiculo
  detalhesAnatomia?: boolean
  /** Compressão dinâmica dianteira em metros (positivo = comprimida/mergulho) */
  compressaoDianteira?: number
  /** Compressão dinâmica traseira em metros (positivo = comprimida) */
  compressaoTraseira?: number
  /** Destaque visual para modo didático de anatomia veicular */
  destaque?: boolean
}

/**
 * Coluna de suspensão simplificada e de alta performance (baixo consumo poligonal):
 * - Cilindro telescópico de amortecedor em aço cromado.
 * - Anéis concêntricos representando as espiras da mola helicoidal.
 * - Prato superior de fixação na torre do monobloco e prato inferior articulado no cubo da roda.
 * - Compressão dinâmica visível nas 4 caixas de roda com suporte a mergulho (dive/pitch).
 */
export function SuspensaoVeicular({
  perfil,
  compressaoDianteira = 0,
  compressaoTraseira = 0,
  destaque = false,
  detalhesAnatomia = false,
}: Props) {
  // Posições dos 4 conjuntos nas caixas de roda
  const xDist = perfil.largura / 2 - perfil.larguraRoda - 0.04
  const yBase = perfil.raioRoda
  const yTopo = perfil.topoSoleiraY + 0.32
  const alturaLivre = Math.max(0.18, yTopo - yBase)

  const corMola = destaque ? '#f0c24b' : '#8094a8'
  const corAmortecedor = '#94a3b8'

  const conteudo = (
    <group>
      {[-xDist, xDist].flatMap((x) =>
        [
          { z: perfil.eixoDianteiroZ, comp: compressaoDianteira, eixo: 'dianteira' },
          { z: perfil.eixoTraseiroZ, comp: compressaoTraseira, eixo: 'traseira' },
        ].map(({ z, comp, eixo }) => {
          const compressaoEfetiva = Math.max(-0.06, Math.min(0.09, comp))
          const alturaAtual = alturaLivre - compressaoEfetiva
          const xCubo = Math.sign(x) * (perfil.largura / 2 - perfil.larguraRoda / 2 + 0.02)
          const passos = 5
          const espacamento = (alturaAtual * 0.72) / (passos + 1)
          const espirasY = Array.from({ length: passos }, (_, i) => -alturaAtual / 2 + (i + 1) * espacamento)

          return (
            <ConjuntoSuspensao key={`${x}:${z}`} anatomia={detalhesAnatomia} id={`suspensao.${eixo}`} x={x}>
            <group name={`conjunto-suspensao-${eixo}`}>
            <LigacaoSuspensao de={[x * 0.62, perfil.pisoY + 0.02, z]} para={[xCubo, yBase, z]} />
            <LigacaoSuspensao de={[x, yBase, z]} para={[xCubo, yBase, z]} />
            <group position={[x, yBase + alturaAtual / 2, z]}>
            {/* Haste interna do amortecedor telescópico (aço cromado) */}
            <mesh position={[0, 0, 0]}>
              <cylinderGeometry args={[0.016, 0.016, alturaAtual, 10]} />
              <meshStandardMaterial
                color={corAmortecedor}
                roughness={0.15}
                metalness={0.92}
              />
            </mesh>

            {/* Corpo externo / cartucho do amortecedor inferior */}
            <mesh position={[0, -alturaAtual * 0.25, 0]}>
              <cylinderGeometry args={[0.024, 0.024, alturaAtual * 0.5, 10]} />
              <meshStandardMaterial
                color="#1e293b"
                roughness={0.6}
                metalness={0.4}
              />
            </mesh>

            {/* Prato inferior de sustentação da mola */}
            <mesh position={[0, -alturaAtual * 0.38, 0]}>
              <cylinderGeometry args={[0.052, 0.052, 0.016, 12]} />
              <meshStandardMaterial
                color="#0f172a"
                roughness={0.5}
                metalness={0.6}
              />
            </mesh>

            {/* Prato superior de fixação na torre do monobloco */}
            <mesh position={[0, alturaAtual * 0.44, 0]}>
              <cylinderGeometry args={[0.055, 0.055, 0.018, 12]} />
              <meshStandardMaterial
                color="#0f172a"
                roughness={0.5}
                metalness={0.6}
              />
            </mesh>

            {/* Anéis concêntricos representando as espiras da mola helicoidal */}
            {espirasY.map((ey, i) => (
              <mesh
                key={i}
                position={[0, ey, 0]}
                rotation={[Math.PI / 2, 0, 0]}
              >
                <torusGeometry args={[0.046, 0.008, 6, 12]} />
                <meshStandardMaterial
                  color={corMola}
                  roughness={0.3}
                  metalness={0.7}
                />
              </mesh>
            ))}
            </group>
          </group>
          </ConjuntoSuspensao>
        )
      }),
    )}
    </group>
  )
  return detalhesAnatomia ? <Peca id="suspensao">{conteudo}</Peca> : conteudo
}
